/*
 * reveal.js — dependency-free scroll-reveal controller for She Formal.
 *
 * Marks any [data-reveal] element visible (adds `.is-visible`) the moment it nears
 * the viewport, using a single shared IntersectionObserver. Direct [data-reveal]
 * children of a [data-reveal-group] receive an inline `--reveal-index` so they
 * stagger in order. Respects prefers-reduced-motion by revealing everything
 * immediately and skipping the observer entirely.
 *
 * Public API (markup only — no JS calls required):
 *   <div data-reveal> … </div>
 *   <ul data-reveal-group>
 *     <li data-reveal> … </li>   ← --reveal-index set automatically (0, 1, 2 …)
 *   </ul>
 */
(() => {
  'use strict';

  /** Trigger slightly before the element fully enters the viewport. */
  const ROOT_MARGIN = '0px 0px -10% 0px';
  const VISIBLE_CLASS = 'is-visible';
  const REVEAL_SELECTOR = '[data-reveal]';
  const GROUP_SELECTOR = '[data-reveal-group]';

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Reveal an element immediately (no observation).
   * @param {Element} el
   */
  const revealNow = (el) => el.classList.add(VISIBLE_CLASS);

  /**
   * Assign per-child stagger indices for a reveal group.
   * @param {Element} group - Element carrying [data-reveal-group].
   */
  const indexGroup = (group) => {
    let index = 0;
    for (const child of group.children) {
      if (!(child instanceof HTMLElement) || !child.hasAttribute('data-reveal')) continue;
      child.style.setProperty('--reveal-index', String(index));
      index += 1;
    }
  };

  // Reduced motion: nothing to animate. CSS already shows everything; just exit.
  if (prefersReducedMotion) return;

  // No IntersectionObserver support: fail open by revealing everything.
  if (!('IntersectionObserver' in window)) {
    const fallbackReveal = () => {
      for (const el of document.querySelectorAll(REVEAL_SELECTOR)) revealNow(el);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fallbackReveal, { once: true });
    } else {
      fallbackReveal();
    }
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        revealNow(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: ROOT_MARGIN, threshold: 0 }
  );

  /**
   * Register a single [data-reveal] element with the shared observer.
   * @param {Element} el
   */
  const observe = (el) => {
    if (el.classList.contains(VISIBLE_CLASS)) return;
    observer.observe(el);
  };

  /**
   * Index groups and observe every [data-reveal] within a subtree.
   * @param {ParentNode} root
   */
  const scan = (root) => {
    for (const group of root.querySelectorAll(GROUP_SELECTOR)) indexGroup(group);
    for (const el of root.querySelectorAll(REVEAL_SELECTOR)) observe(el);

    // The root itself may be a reveal target / group when added dynamically.
    if (root instanceof HTMLElement) {
      if (root.matches(GROUP_SELECTOR)) indexGroup(root);
      if (root.matches(REVEAL_SELECTOR)) observe(root);
    }
  };

  /**
   * Opt every top-level storefront section into a gentle section-level reveal,
   * except the first (above the fold — must paint immediately) and any section
   * that already declares its own [data-reveal] (keeps its bespoke stagger).
   */
  const autoTagSections = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;
    let position = 0;
    for (const section of main.querySelectorAll(':scope > .shopify-section')) {
      position += 1;
      if (position === 1) continue;
      if (section.hasAttribute('data-reveal')) continue;
      if (section.querySelector(REVEAL_SELECTOR)) continue;
      section.setAttribute('data-reveal', '');
    }
  };

  const init = () => {
    autoTagSections();
    scan(document);

    // Pick up sections re-rendered by the theme editor or async navigation.
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) scan(/** @type {Element} */ (node));
        }
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
